import React, { useMemo, useState } from 'react';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import { getSpeakerName, isStructuredTranscript } from './transcriptUtils';

/**
 * Read-only transcript: speaker turns for structured transcripts, wrapped
 * text otherwise. Long transcripts scroll inside the box.
 *
 * @param {object} props
 * @param {object|null} props.transcript - Normalised transcript (see normaliseTranscript).
 * @param {number|string} [props.maxHeight=320] - Max height before scrolling.
 * @param {boolean} [props.searchable=false] - Show a filter box for long transcripts.
 * @returns {JSX.Element}
 */
function TranscriptView({ transcript, maxHeight = 320, searchable = false }) {
  const [query, setQuery] = useState('');
  const structured = isStructuredTranscript(transcript);
  const q = query.trim().toLowerCase();

  const utterances = useMemo(() => {
    if (!structured) return [];
    const all = transcript.transcript.map((u, index) => ({ u, index }));
    if (!q) return all;
    return all.filter(({ u }) =>
      `${getSpeakerName(u)} ${u.text || u.content || ''}`.toLowerCase().includes(q)
    );
  }, [structured, transcript, q]);

  const plainText = !structured
    ? (typeof transcript === 'string' ? transcript : transcript?.content || '')
    : '';
  const plainLines = useMemo(() => {
    if (structured || !q) return null;
    return plainText.split('\n').filter((line) => line.toLowerCase().includes(q));
  }, [structured, plainText, q]);

  return (
    <Box>
      {searchable && (
        <TextField
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find in transcript"
          inputProps={{ 'aria-label': 'Find in transcript' }}
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}
      <Box
        tabIndex={0}
        aria-label="Transcript"
        sx={{
          maxHeight,
          overflow: 'auto',
          p: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 'var(--q-radius-xl)',
          bgcolor: 'var(--q-bg-subtle)',
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
        }}
      >
        {structured ? (
          utterances.length ? (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {utterances.map(({ u, index }) => (
                <Box key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--q-accent-text)' }}>
                      {getSpeakerName(u)}
                    </Typography>
                    {u.timestamp && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {u.timestamp}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ color: 'var(--q-text-doc)' }}>
                    {u.text || u.content || ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No lines match.</Typography>
          )
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'var(--q-text-doc)' }}>
            {plainLines
              ? (plainLines.length ? plainLines.join('\n') : 'No lines match.')
              : (plainText || 'No transcript text.')}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default TranscriptView;
